'use client';

import type { ChainId } from '@morpho-org/blue-sdk';
import { type ActionRequirement, type BaseAction, type BlueAuthorizationAction, type ERC20ApprovalAction, isRequirementApproval, isRequirementBlueAuthorization, isRequirementSignature, type RequirementSignature, type SignatureRequirement, type Transaction } from '@morpho-org/morpho-sdk';
import { formatLongString, Time } from '@morpho-org/morpho-ts';
import { NotificationStatus, useToastContext } from '@morpho-org/ui-v6/contexts';
import { SCREEN_ADDRESS_ROUTE, SCREENING_BLOCKED_MESSAGE, SCREENING_UNAVAILABLE_MESSAGE } from '@repo/address-screening/client';
import { useMandatoryContext } from '@repo/hooks';
import { withTimeout } from '@repo/utils';
import { checkSafeProposal, ensureAuthorizedUser, fetchSafeWalletHealth, findSafeProposal, findSafeProposalByHash, type SafeProposal, type SafeProposalOutcome, type ScreeningVerdict, sendWalletTransaction } from '@repo/web3';
import { classifyGasEstimateError, GAS_ESTIMATE_TIMEOUT_MS, walletManagesGas } from '@repo/web3/utils';
import SafeAppsSDK from '@safe-global/safe-apps-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { getPublicClient } from '@wagmi/core';
import { createContext, type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { type Address, BaseError, type Chain, type Hash, type Hex, InternalRpcError, isAddressEqual, isHash, UnknownRpcError, type WalletClient } from 'viem';
import { useConfig } from 'wagmi';
import type { TransactionGasEstimateError, TransactionGasLimitSource, VaultExitType } from '@/app/api/t/constants';
import { clientEnvs } from '@/config';
import { withDeploymentParam } from '@/config/deployment';
import { Status } from '@/contexts/Transaction/StateMachine/helpers';
import { type MorphoAccount, useWeb3Context } from '@/contexts/Web3Context';
import { addTransactionGasBuffer } from '@/helpers/gasBuffer';
import { safeSentryBreadcrumb } from '@/helpers/sentry';
import { type CheckoutAttemptTrackingContext, maybeTrackStepResolved, trackCheckoutStepFailure, trackReviewConfirmed, trackTransactionFlowCancelled } from '@/lib/analytics/client/trackers/checkout-funnel';
import { trackTransactionConfirmed, trackTransactionFailed, trackTransactionInitiated, trackTransactionSigned } from '@/lib/analytics/client/trackers/transaction';
import { getVaultExitType, trackTransaction, type TrackTransactionParams } from '@/lib/observability/client/trackers/transaction';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { FeatureFlagEnvironment, type FeatureFlags, getTxFlowTracking, type TxSourceFlow } from '@/lib/shared/utils/flags.constants';
import { InterfaceOperation } from '@/operations/operations.types';
import { checkSimulation, isSimulationUnsupportedSender, SimulationError, UNVERIFIED_SIMULATION_CODES } from '@/services/simulate/simulate';
import { buildSimulationLimits } from '@/services/simulate/simulationLimits';
import { describeRequirement } from './describeRequirement';
import { getReceiptPollingClient, type OnchainGasLookup, readOnchainGasLimit, type ReceiptResult, waitForReceiptWithReplacementDetection } from './getReceiptPollingClient';
import { logTransactionError } from './logTransactionError';
import { SafeProposalDroppedError } from './transaction-errors';
interface Web3Params {
  client: WalletClient;
  account: MorphoAccount & {
    address: Address;
  };
  chain: Chain;
}

/** Claims tx_outcome dedupe before the best-effort on-chain gas lookup resolves. */
function trackTransactionOutcome(params: TrackTransactionParams, onchainGasLookup?: OnchainGasLookup): void {
  trackTransaction(params, onchainGasLookup && readOnchainGasLimit(onchainGasLookup));
}
const getSimulationTrackingOptions = (checkoutTracking: CheckoutAttemptTrackingContext | undefined) => {
  const reviewSessionId = checkoutTracking?.reviewSessionId;
  return reviewSessionId === undefined ? {} : {
    tracking: {
      reviewSessionId
    }
  };
};
const assertContractWalletRequirements = (account: Pick<MorphoAccount, 'isContract'>, requirements: ReadonlyArray<BuiltTxRequirement | ActionRequirement>) => {
  if (account.isContract && requirements.some(isRequirementSignature)) {
    throw new Error("This wallet can't sign approvals. Please review the transaction again.");
  }
};
const WALLET_CONNECT_CONNECTOR_ID = 'walletConnect';

// Endpoint carries the `?dpl=` pin (@repo/web3 has no header seam); computed once so
// connect-time (`Web3Provider`) and tx-time screening share the same react-query key.
const SCREEN_ADDRESS_ENDPOINT = withDeploymentParam(SCREEN_ADDRESS_ROUTE);

// WalletConnect is one wagmi connector for every peer wallet; suffix the peer name so the
// dashboard can tell Rabby-via-WC from Ledger Live-via-WC.
const getWalletConnector = (account: MorphoAccount) => {
  const connectorId = account.connector?.id;
  if (connectorId === WALLET_CONNECT_CONNECTOR_ID && account.walletConnectPeerName) return `${connectorId}:${account.walletConnectPeerName}`;
  return connectorId;
};
interface GasTelemetry {
  gasEstimate?: number;
  gasLimit?: number;
  gasUsed?: number;
  gasLimitSource: TransactionGasLimitSource;
  /** True when the confirmed receipt came from a cancel/replace (different calldata), so its
   * `gasUsed` no longer measures the estimated tx and must be excluded from buffer-utilization. */
  gasReceiptReplaced?: boolean;
  /** Why the estimate failed; set only when `gasLimitSource` is 'estimate_failed'. */
  gasEstimateError?: TransactionGasEstimateError;
}
type GasTransaction = {
  gas?: bigint;
};
export interface TransactionBuilder {
  // Read-only base-action form so extension `migrateToX` bundles type-check
  // without `as unknown as ...` casts — consumers below only read to/value/data.
  buildTx: (signatures?: readonly RequirementSignature[]) => Readonly<Transaction<BaseAction> & GasTransaction>;
}

/**
 * A built (on-chain) requirement transaction sent before the final tx: an ERC20
 * approval, or a Morpho `setAuthorization` (needed by morpho-sdk borrow /
 * withdraw-collateral / combos so GeneralAdapter1 can act on behalf of the user).
 * Both are sent identically; only the signature `Requirement` is signed instead.
 */
export type BuiltTxRequirement = (Transaction<ERC20ApprovalAction> & GasTransaction) | (Transaction<BlueAuthorizationAction> & GasTransaction);

// For vault withdraw-family operations the built tx's SDK action names the exit
// (withdraw / redeem / force variants). `buildTx()` unsigned is what the
// simulated-position hook already runs, and an analytics read must never break submit.
const resolveVaultExitType = (transaction: TransactionBuilder, actionType?: string): VaultExitType | undefined => {
  if (actionType !== InterfaceOperation.InterfaceOperationType.vaultV2Withdraw && actionType !== InterfaceOperation.InterfaceOperationType.metaMorphoWithdraw) return undefined;
  try {
    return getVaultExitType(transaction.buildTx().action.type);
  } catch {
    return undefined;
  }
};
export interface ActiveTransaction {
  requirements?: readonly (BuiltTxRequirement | ActionRequirement)[];
  transaction: TransactionBuilder;
  actionType?: string;
  sdkAction?: string;
  /** The tx* flag flow this dialog belongs to; absent for flows without one. */
  flow?: TxSourceFlow;
  /** Require the exact final bundle to simulate successfully and disallow retry bypasses. */
  requiresSuccessfulSimulation?: boolean;
  /** Skip the pre-sign simulation of the EOA final transaction, including on retry. Safe still simulates, since its approvals and final tx run as one bundle. */
  skipFinalSimulation?: boolean;
  /** Persisted so retry() re-runs it: without this a tx that fails then succeeds on retry
   * would skip its success side-effects (balance refresh, auth-cache invalidation, …).
   * Receives the confirmed receipt so a flow whose success screen depends on what actually
   * landed (a cancellation and a same-nonce replacement confirm too) can inspect it. */
  onTxSuccess?: (receipt?: ReceiptResult) => void;
}
interface ExecuteTransactionParams extends ActiveTransaction {
  chain: Chain;
  account: MorphoAccount;
  actionType?: string;
  checkoutTracking?: CheckoutAttemptTrackingContext;
}
interface InternalExecuteParams extends ExecuteTransactionParams {
  /** Step to resume from on retry */
  startStep?: number | 'final';
  /** Skip simulation check (used when user accepts to continue despite simulation failure) */
  skipSimulation?: boolean;
  /** A retry resuming an already-entered flow — never a fresh entry, even when it resumes at
   * step 0 (a first-requirement failure). The once-on-entry screen keys off this, not the resume
   * index, so a first-step retry can't re-run the gate and get blocked by a transient outage. */
  isRetry?: boolean;
}
export interface RetryOptions {
  skipSimulation?: boolean;
}
export interface TransactionContextV2Props {
  activeTransactions?: ActiveTransaction;
  checkoutTracking?: CheckoutAttemptTrackingContext;
  submitTransactions: (client: WalletClient, params: ExecuteTransactionParams) => Promise<void>;
  reset: () => void;
  retry: (client: WalletClient, options?: RetryOptions) => Promise<void>;
  /**
   * Re-run a FRESH screen after a transaction-time screening outage (`screeningError` is
   * `SCREENING_UNAVAILABLE_MESSAGE`). Distinct from `retry`, which resumes an already-screened
   * attempt and skips the gate; this re-enters the flow so the signer is screened again.
   */
  retryScreening: (client: WalletClient) => Promise<void>;
  txHash: Hash | undefined;
  /** Receipt of the confirmed final transaction — undefined until it lands. */
  receipt: ReceiptResult | undefined;
  chainId: ChainId;
  account: MorphoAccount | undefined;
  retryDisabled: boolean;
  status: Status | undefined;
  step: number | 'final';
  /**
   * Whether the current error is an acceptable simulation error that can be bypassed.
   * True if the error is a SimulationError whose code is not in `NON_BYPASSABLE_SIMULATION_CODES`.
   */
  isAcceptableSimulationError: boolean;
  signatureDeadline: number | undefined;
  /** Set when a signature revert reports a stale nonce; retry is blocked until the flow is reset. */
  isStaleNonceError: boolean;
  /** Set when the final simulation rejects the bundle on its v5 slippage limits; never bypassable. */
  isLimitViolationError: boolean;
  /** Set when the final simulation's outcome couldn't be verified (malformed response, missing evidence). */
  isUnverifiedSimulationError: boolean;
  /** Set when the wallet proposed the tx to a Safe instead of broadcasting; cleared on reset and fresh submit. */
  safeProposalMessage: string | undefined;
  /**
   * True while a transaction proposed to a Safe (e.g. Rabby in front of a Safe) waits for its
   * owners to execute it. The flow tracks that exact Safe transaction and resumes on its own once
   * it executes. Cleared when it settles, on reset and on fresh submit.
   */
  isAwaitingSafeExecution: boolean;
  /**
   * True while a Safe proposal is being tracked but its status checks keep failing (Safe
   * Transaction Service or RPC unreachable). Tracking keeps the proposal and retries; cleared
   * once a check succeeds or the wait ends.
   */
  isSafeStatusUnavailable: boolean;
  /**
   * Set when the final transaction was broadcast but its receipt never arrived before the
   * polling deadline. Its outcome is unknown and it may still confirm, so retry is blocked (a
   * resubmit would duplicate the bundle — and reuse its Permit2 nonce). The hash keeps being
   * watched for TX_RECEIPT_BACKGROUND_TIMEOUT, so a late receipt still settles the flow and
   * clears the flag; otherwise cleared on every fresh submit and on reset.
   */
  isUnconfirmedTransaction: boolean;
  /**
   * Set when the connected signer fails transaction-time address screening. The confirm dialog
   * surfaces it and the flow issues no wallet prompt. Cleared on every fresh submit and on reset.
   */
  screeningError: string | undefined;
  /**
   * True while a transaction-time screening verdict is in flight (before a wallet prompt). The
   * confirm dialog renders a verifying state — never a stale terminal/complete one — and attributes
   * a manual cancel to the 'screening' step. Cleared as soon as the verdict resolves and on reset.
   */
  isScreening: boolean;
}
interface ProcessRequirementsParams {
  requirements: readonly (BuiltTxRequirement | ActionRequirement)[];
  startStep: number;
  web3Params: Web3Params;
  /** `actionType` of the flow's final transaction, stamped on its approval/signature steps */
  flowActionType?: string;
  flowTracking: Pick<TrackTransactionParams, 'flow' | 'txFlag' | 'sdkAction'>;
  /**
   * Returns true once this submit has been superseded (cancel/reset/new submit bumped the
   * generation). Checked after every awaited signature/approval so a wallet prompt resolved
   * AFTER the user cancelled cannot advance the flow into the next requirement or the final tx.
   */
  isStaleSubmit?: () => boolean;
  isSupersededSubmit?: () => boolean;
}
export const TransactionContextV2 = createContext<TransactionContextV2Props | null>(null);
export const useTransactionContextV2 = () => {
  return useMandatoryContext(TransactionContextV2, "TransactionContextV2");
};

/** Delay before checking transaction receipt (ensures RPC indexing) */
const TX_INDEX_DELAY = 500;

/** Delay between receipt polling attempts */
const TX_RECEIPT_RETRY_DELAY = 3_000;

/** Maximum time to wait for a transaction receipt before timing out */
export const TX_RECEIPT_TIMEOUT = 360_000;

/** After the receipt poll times out, keep watching the broadcast hash this much longer so a late confirmation still settles the flow. */
const TX_RECEIPT_BACKGROUND_TIMEOUT = 1_800_000;

/** Delay before re-enabling retry button */
const RETRY_COOLDOWN = 2_000;

/** Delay between tracking rounds while a transaction proposed to a Safe waits for execution */
const SAFE_PROPOSAL_POLL_INTERVAL = 5_000;
const SAFE_PROPOSAL_CHECK_FAILURE_THRESHOLD = 5;
const SAFE_PROPOSAL_LOOKUP_TIMEOUT = 15_000;
export class WalletSendResultError extends Error {
  constructor(result: unknown) {
    const bytes = typeof result === 'string' && result.startsWith('0x') ? (result.length - 2) / 2 : undefined;
    super(bytes === 65 ? 'Signature returned instead of a transaction hash. Check your wallet first.' : 'Unexpected response instead of a transaction hash. Check your wallet first.');
    this.name = 'WalletSendResultError';
  }
}

/** The receipt confirmed the transaction reverted onchain — the outcome is known, retry is safe. */
export class TransactionRevertedError extends Error {
  constructor(/** The mined, reverted on-chain hash. */
  public readonly hash: Hash) {
    super(`Transaction failed: ${hash}`);
    this.name = 'TransactionRevertedError';
  }
}

/** The non-hash result a wallet can return when it proposed the tx to a Safe instead of
 * broadcasting it (e.g. Rabby in front of a Safe). Extends WalletSendResultError so the retry
 * and stale-submit branches keyed on the parent class apply unchanged. */
export class SafeProposalPendingError extends WalletSendResultError {
  constructor(result: unknown, step: 'approval' | 'final') {
    super(result);
    this.message = step === 'approval' ? 'Approval proposed to your Safe. Execute it from your Safe queue, then start over to finish.' : 'Transaction proposed to your Safe. Confirm and execute it from your Safe queue.';
    this.name = 'SafeProposalPendingError';
  }
}
const isWalletSendResultHash = (result: unknown): result is Hash => typeof result === 'string' && isHash(result);

/**
 * Formats an error for display in a toast notification.
 *
 * viem's `message` bundles request arguments, docs link and version; only `shortMessage` and
 * `details` are meant for a reader. For the generic RPC wrappers the `shortMessage` is a
 * constant, so `details` (the wallet's or node's own words) is the only part that says what
 * happened.
 */
export function formatErrorMessage(error: unknown): string {
  const message = error instanceof BaseError ? describeViemError(error) : error instanceof Error ? error.message : 'An unknown error occurred';
  return formatLongString(message, 100);
}
function describeViemError(error: BaseError): string {
  const isOpaqueRpcError = error.walk(e => e instanceof UnknownRpcError || e instanceof InternalRpcError) !== null;
  if (isOpaqueRpcError && error.details) {
    const details = formatLongString(error.details.replace(/[.\s]+$/, ''), 70);
    return `Request failed: ${details}. Try again.`;
  }
  return error.shortMessage || error.message;
}
export type TransactionV2ProviderProps = PropsWithChildren;
const getFlowTracking = (flags: FeatureFlags, flow: TxSourceFlow | undefined): Pick<TrackTransactionParams, 'flow' | 'txFlag'> => flow ? getTxFlowTracking(flags, flow) : {};
const TransactionContextV2Provider = ({
  children
}: TransactionV2ProviderProps) => {
  const {
    targetChain,
    account: targetAccount
  } = useWeb3Context();
  const {
    displayToast,
    displayTxToast
  } = useToastContext();
  const flags = useFeatureFlags();
  const {
    evmPreflightTransactionSimulation: isSimulationEnabled
  } = flags;
  const wagmiConfig = useConfig();
  const queryClient = useQueryClient();
  const [activeTransactions, setActiveTransactions] = useState<ActiveTransaction | undefined>();
  const [checkoutTracking, setCheckoutTracking] = useState<CheckoutAttemptTrackingContext | undefined>();
  const checkoutTrackingRef = useRef<CheckoutAttemptTrackingContext | undefined>(undefined);
  // Monotonic token identifying the current submit. Bumped on every submit and on
  // reset() so an in-flight submit can detect that it was canceled mid-await.
  const submitGenerationRef = useRef(0);
  // `submitGenerationRef` is bumped by `reset()` too, so it answers "is this flow still on
  // screen?". Once a transaction is broadcast that is the wrong question: the money has moved,
  // and closing the dialog must not cost the user the confirmation tracking or the data
  // refetch. This one advances only when another submit starts — "was I superseded?".
  const submitSequenceRef = useRef(0);
  const [retryDisabled, setRetryDisabled] = useState(false);
  const [retryBlocked, setRetryBlocked] = useState(false);
  const [isStaleNonceError, setIsStaleNonceError] = useState(false);
  const [isLimitViolationError, setIsLimitViolationError] = useState(false);
  const [isUnverifiedSimulationError, setIsUnverifiedSimulationError] = useState(false);
  const [isUnconfirmedTransaction, setIsUnconfirmedTransaction] = useState(false);
  const [safeProposalMessage, setSafeProposalMessage] = useState<string | undefined>();
  const [isAwaitingSafeExecution, setIsAwaitingSafeExecution] = useState(false);
  const [isSafeStatusUnavailable, setIsSafeStatusUnavailable] = useState(false);
  const [step, setStep] = useState<number | 'final'>(0);
  const [status, setStatus] = useState<Status | undefined>();
  const [isAcceptableSimulationError, setIsAcceptableSimulationError] = useState(false);
  const cachedSignatureRequirements = useRef<RequirementSignature[]>([]);
  // Indexes of approval/setAuthorization requirements already confirmed on-chain in this flow,
  // so a rewind past them (invalid_signature retry) doesn't re-broadcast them.
  const completedOnChainSteps = useRef<Set<number>>(new Set());
  const minedRequirementBlock = useRef<bigint | undefined>(undefined);
  // Mined requirement txs the final simulation waits for on the simulation backend.
  const minedRequirementTxHashes = useRef<Hex[]>([]);
  const [chain, setChain] = useState<Chain>(targetChain);
  const [account, setAccount] = useState<MorphoAccount | undefined>(targetAccount);
  const [txHash, setTxHash] = useState<Hash | undefined>();
  const [receipt, setReceipt] = useState<ReceiptResult | undefined>();
  const [signatureDeadline, setSignatureDeadline] = useState<number | undefined>();
  const [screeningError, setScreeningError] = useState<string | undefined>();
  const [isScreening, setIsScreening] = useState(false);
  const applyGasBuffer = async <T extends {
    gas?: bigint;
  },>(tx: T, web3Params: Web3Params): Promise<{
    tx: T;
    telemetry: GasTelemetry;
  }> => {
    const {
      account: account_0,
      chain: txChain
    } = web3Params;
    if (walletManagesGas({
      connectorId: account_0.connector?.id,
      isSafeWallet: account_0.isSafe
    })) {
      return {
        tx,
        telemetry: {
          gasLimitSource: 'wallet'
        }
      };
    }
    if (tx.gas !== undefined) {
      return {
        tx,
        telemetry: {
          gasLimitSource: 'builder',
          gasLimit: Number(tx.gas)
        }
      };
    }
    try {
      const publicClient = getPublicClient(wagmiConfig, {
        chainId: txChain.id
      });
      if (!publicClient) {
        // Wanted to buffer but have no client to estimate with — a different signal from a
        // deliberate wallet-managed send, so keep it out of the `wallet` bucket. Breadcrumb it with
        // a reason: a whole chain losing its public client would otherwise be a silent lump of
        // `estimate_failed` with no pointer to why.
        safeSentryBreadcrumb({
          category: 'transaction',
          message: 'Transaction gas buffering unavailable',
          level: 'warning',
          data: {
            chainId: txChain.id,
            reason: 'no_public_client'
          }
        });
        return {
          tx,
          telemetry: {
            gasLimitSource: 'estimate_failed',
            gasEstimateError: 'no_public_client'
          }
        };
      }
      const gasEstimate = await withTimeout(publicClient.estimateGas({
        ...tx,
        account: account_0.address
      }), GAS_ESTIMATE_TIMEOUT_MS);
      const gas = addTransactionGasBuffer(gasEstimate);
      return {
        tx: {
          ...tx,
          gas
        },
        telemetry: {
          gasEstimate: Number(gasEstimate),
          gasLimit: Number(gas),
          gasLimitSource: 'app_buffered'
        }
      };
    } catch (error) {
      // Estimate threw or timed out — a failed attempt to buffer, not a wallet-managed choice.
      const gasEstimateError = classifyGasEstimateError(error);
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Transaction gas buffering unavailable',
        level: 'warning',
        data: {
          chainId: txChain.id,
          reason: 'estimate_error',
          gasEstimateError,
          error: String(error)
        }
      });
      return {
        tx,
        telemetry: {
          gasLimitSource: 'estimate_failed',
          gasEstimateError
        }
      };
    }
  };
  const showErrorToast = (params: {
    error: unknown;
    hash?: Hash;
    chainId?: number;
    userIsContract?: boolean;
  }) => {
    const {
      error: error_0,
      hash,
      chainId,
      userIsContract
    } = params;
    if (hash && chainId !== undefined) {
      displayTxToast({
        notificationStatus: NotificationStatus.error,
        txHash: hash,
        chainId,
        userIsContract: userIsContract ?? false
      });
    } else {
      displayToast({
        description: formatErrorMessage(error_0),
        notificationStatus: NotificationStatus.error
      });
    }
  };

  /**
   * Validates that client chain and account match expected values.
   * @throws Error if there's a mismatch
   */
  function validateClientParams(params_0: {
    client: WalletClient;
    chain: Chain;
    account: MorphoAccount;
  }): Web3Params {
    const {
      client,
      chain: chain_0,
      account: account_1
    } = params_0;
    let error_1 = undefined;
    if (client.chain?.id !== chain_0.id) {
      error_1 = new Error(`Chain ID mismatch: expected ${chain_0.id}, received ${client.chain?.id}`);
      showErrorToast({
        error: error_1
      });
      throw error_1;
    }
    if (!account_1.address || !client.account?.address) {
      error_1 = new Error(`Address is missing: account ${account_1.address} or client ${client.account?.address}`);
      showErrorToast({
        error: error_1
      });
      throw error_1;
    }
    if (!isAddressEqual(client.account.address, account_1.address)) {
      error_1 = new Error(`Account mismatch: expected ${account_1.address}, received ${client.account.address}`);
      showErrorToast({
        error: error_1
      });
      throw error_1;
    }
    return {
      client,
      chain: chain_0,
      account: {
        ...account_1,
        address: account_1.address
      }
    };
  }

  /**
   * Polls a Safe proposal until it settles (see checkSafeProposal). Ends quietly when the submit
   * goes stale; the proposal stays recorded so a later submit can resume it.
   * @returns the onchain transaction that executed it, or undefined when the submit went stale.
   * @throws TransactionRevertedError when the Safe transaction failed, SafeProposalDroppedError
   * when it was rejected, replaced or deleted from the queue.
   */
  const waitForSafeProposal = async (proposal: SafeProposal, {
    chain: txChain_0
  }: Web3Params, isStaleSubmit?: () => boolean): Promise<{
    hash: Hash;
  } | undefined> => {
    if (isStaleSubmit?.()) return undefined;
    setIsAwaitingSafeExecution(true);
    setStatus(Status.pending);
    let consecutiveCheckFailures = 0;
    try {
      while (!isStaleSubmit?.()) {
        let outcome: SafeProposalOutcome | undefined;
        try {
          const publicClient = getPublicClient(wagmiConfig, {
            chainId: txChain_0.id
          });
          if (!publicClient) throw new Error(`No public client for chain ${txChain_0.id}`);
          outcome = await checkSafeProposal({
            client: publicClient,
            chainId: txChain_0.id,
            proposal
          });
          consecutiveCheckFailures = 0;
          if (!isStaleSubmit?.()) setIsSafeStatusUnavailable(false);
        } catch {
          consecutiveCheckFailures++;
          if (consecutiveCheckFailures === SAFE_PROPOSAL_CHECK_FAILURE_THRESHOLD) {
            safeSentryBreadcrumb({
              category: 'transaction',
              message: 'Safe proposal checks failing',
              level: 'warning',
              data: {
                chainId: txChain_0.id,
                consecutiveCheckFailures
              }
            });
            if (!isStaleSubmit?.()) setIsSafeStatusUnavailable(true);
          }
        }
        if (outcome && !isStaleSubmit?.()) {
          safeSentryBreadcrumb({
            category: 'transaction',
            message: `Safe proposal ${outcome.status}`,
            level: outcome.status === 'executed' ? 'info' : 'warning',
            data: {
              chainId: txChain_0.id,
              safeTxHash: proposal.safeTxHash
            }
          });
          if (outcome.status === 'dropped') throw new SafeProposalDroppedError();
          if (outcome.status === 'failed') throw new TransactionRevertedError(outcome.hash);
          return outcome;
        }
        await Time.wait(SAFE_PROPOSAL_POLL_INTERVAL);
      }
      return undefined;
    } finally {
      if (!isStaleSubmit?.()) {
        setIsAwaitingSafeExecution(false);
        setIsSafeStatusUnavailable(false);
      }
    }
  };

  /**
   * Narrows a wallet's sendTransaction result to the hash of the transaction that carries it
   * onchain. A non-hash result may be a 65-byte signature, or a Safe wallet contract may return
   * its 32-byte safeTxHash instead of a transaction hash. Both Safe proposal forms are resolved
   * to their exact Safe transaction and tracked until execution, so the caller carries on with
   * the execution's hash. Unresolvable signatures surface SafeProposalPendingError; an
   * unresolvable safeTxHash falls back to the current hash behavior. Safe health probes are
   * bounded by the same 3s deadline as gas estimation.
   * @returns undefined when a resolved Safe proposal belongs to a stale submit.
   */
  const resolveWalletSendResult = async (params_1: {
    result: unknown;
    tx: {
      to: Address;
      data?: Hex;
      value?: bigint;
    };
    web3Params: Web3Params;
    step: 'approval' | 'final';
    isStaleSubmit?: () => boolean;
  }): Promise<Hash | undefined> => {
    const {
      result,
      tx: tx_0,
      web3Params: web3Params_0,
      step: step_0,
      isStaleSubmit: isStaleSubmit_0
    } = params_1;
    const isHashResult = isWalletSendResultHash(result);

    // Only a 65-byte result is a signature — i.e. possibly a Safe proposal. Other non-hash
    // results are genuine wallet bugs and go straight to the generic error without probing.
    const is65ByteSignature = typeof result === 'string' && result.startsWith('0x') && (result.length - 2) / 2 === 65;
    const {
      account: account_2,
      chain: txChain_1
    } = web3Params_0;
    if (!isHashResult && !is65ByteSignature) throw new WalletSendResultError(result);
    if (isHashResult && !(account_2.isContract && !account_2.isSafe)) return result;
    const publicClient_0 = getPublicClient(wagmiConfig, {
      chainId: txChain_1.id
    });
    let isSafeWallet = account_2.isSafe;
    if (!isSafeWallet) {
      try {
        if (publicClient_0) {
          const health = await withTimeout(fetchSafeWalletHealth(publicClient_0, account_2.address), GAS_ESTIMATE_TIMEOUT_MS);
          isSafeWallet = health.isSafeWallet;
        }
      } catch {
        isSafeWallet = false;
      }
    }
    if (isHashResult && !isSafeWallet) return result;
    if (!isHashResult && !isSafeWallet) throw new WalletSendResultError(result);
    let proposal_0: SafeProposal | undefined;
    if (isHashResult) {
      try {
        proposal_0 = await withTimeout(findSafeProposalByHash({
          chainId: txChain_1.id,
          safe: account_2.address,
          safeTxHash: result,
          tx: tx_0
        }), SAFE_PROPOSAL_LOOKUP_TIMEOUT);
      } catch {
        proposal_0 = undefined;
      }
      if (!proposal_0) {
        safeSentryBreadcrumb({
          category: 'transaction',
          message: 'Safe hash lookup failed, polling returned hash',
          level: 'warning',
          data: {
            chainId: txChain_1.id,
            safeTxHash: result
          }
        });
        return result;
      }
    } else {
      try {
        proposal_0 = publicClient_0 && (await withTimeout(findSafeProposal({
          client: publicClient_0,
          chainId: txChain_1.id,
          safe: account_2.address,
          signature: result as Hex,
          tx: tx_0
        }), SAFE_PROPOSAL_LOOKUP_TIMEOUT));
      } catch {
        proposal_0 = undefined;
      }
      if (!proposal_0) throw new SafeProposalPendingError(result, step_0);
    }
    safeSentryBreadcrumb({
      category: 'transaction',
      message: 'Transaction proposed to Safe',
      level: 'info',
      data: {
        chainId: txChain_1.id,
        step: step_0,
        safeTxHash: proposal_0.safeTxHash
      }
    });
    if (isStaleSubmit_0?.()) {
      if (isHashResult) return undefined;
      throw new SafeProposalPendingError(result, step_0);
    }
    return (await waitForSafeProposal(proposal_0, web3Params_0, isStaleSubmit_0))?.hash;
  };

  /**
   * Sends a transaction and waits for confirmation.
   * @returns The transaction hash on success
   * @throws On transaction failure
   */
  const sendAndConfirmTransaction = async (params_2: {
    tx: Transaction & GasTransaction;
    web3Params: Web3Params;
    onGasTelemetry?: (telemetry: GasTelemetry, onchainGasLookup?: OnchainGasLookup) => void;
    onBroadcast?: (hash_0: Hash) => void;
    isStaleSubmit?: () => boolean;
  }): Promise<Hash | undefined> => {
    const {
      tx: tx_1,
      web3Params: web3Params_1,
      onGasTelemetry,
      onBroadcast,
      isStaleSubmit: isStaleSubmit_1
    } = params_2;
    const {
      client: client_0,
      account: acc,
      chain: txChain_2
    } = web3Params_1;
    const {
      tx: txWithGasBuffer,
      telemetry
    } = await applyGasBuffer(tx_1, web3Params_1);
    onGasTelemetry?.(telemetry);

    // Estimation is an async boundary before the wallet prompt; mirror executeFinalTransaction and
    // bail if a cancel/reset landed during it, so an abandoned approval can't reopen the wallet.
    // Past the broadcast below the money can move, so the existing post-send behaviour is kept.
    if (isStaleSubmit_1?.()) return undefined;
    const sendResult: unknown = await sendWalletTransaction(client_0, {
      ...txWithGasBuffer,
      account: acc.address,
      chain: txChain_2
    }, isStaleSubmit_1);
    const hash_0 = await resolveWalletSendResult({
      result: sendResult,
      tx: tx_1,
      web3Params: web3Params_1,
      step: 'approval',
      isStaleSubmit: isStaleSubmit_1
    });
    if (!hash_0) return undefined;
    setTxHash(hash_0);
    onBroadcast?.(hash_0);
    setStatus(Status.pending);

    // Wait for RPC indexing before polling for receipt
    await Time.wait(TX_INDEX_DELAY);
    const receiptClient = getReceiptPollingClient({
      wagmiConfig,
      walletClient: client_0,
      chainId: txChain_2.id,
      isSafe: false
    });
    // A repriced (sped-up) tx runs the same calldata, so its gasUsed still measures the buffer;
    // a cancel/replace runs different calldata, so its gasUsed is flagged out of the stats.
    let gasReceiptReplaced = false;
    const receipt_0 = await waitForReceiptWithReplacementDetection({
      pollingClient: receiptClient,
      walletClient: client_0,
      hash: hash_0,
      senderAddress: acc.address,
      retryDelay: TX_RECEIPT_RETRY_DELAY,
      timeout: TX_RECEIPT_TIMEOUT,
      onReplaced: replacement => {
        if (replacement.reason !== 'repriced') gasReceiptReplaced = true;
        setTxHash(replacement.transaction.hash);
        onBroadcast?.(replacement.transaction.hash);
        safeSentryBreadcrumb({
          category: 'transaction',
          message: `Transaction ${replacement.reason}`,
          level: 'warning',
          data: {
            originalHash: hash_0,
            newHash: replacement.transaction.hash,
            chainId: txChain_2.id,
            reason: replacement.reason
          }
        });
      }
    });

    // Ensure React state reflects the receipt hash (may differ from the
    // original if the tx was replaced). Defensive against React batching
    // swallowing the onReplaced update in edge cases.
    const confirmedHash = receipt_0.transactionHash ?? hash_0;
    setTxHash(confirmedHash);
    onGasTelemetry?.({
      ...telemetry,
      gasUsed: Number(receipt_0.gasUsed),
      gasReceiptReplaced
    }, acc.isSafe || gasReceiptReplaced ? undefined : {
      client: receiptClient,
      hash: confirmedHash,
      from: acc.address
    });
    if (receipt_0.status !== 'success') {
      throw new TransactionRevertedError(confirmedHash);
    }
    if (!isStaleSubmit_1?.() && (minedRequirementBlock.current === undefined || receipt_0.blockNumber > minedRequirementBlock.current)) {
      minedRequirementBlock.current = receipt_0.blockNumber;
    }
    if (!isStaleSubmit_1?.()) {
      minedRequirementTxHashes.current = [...minedRequirementTxHashes.current, confirmedHash];
    }
    return confirmedHash;
  };

  /**
   * Executes an onchain ERC20 approval transaction.
   */
  const executeApprovalRequirement = async (params_3: {
    requirement: BuiltTxRequirement;
    web3Params: Web3Params;
    stepIndex: number;
    flowActionType?: string;
    flowTracking: Pick<TrackTransactionParams, 'flow' | 'txFlag' | 'sdkAction'>;
    isStaleSubmit?: () => boolean;
    isSupersededSubmit?: () => boolean;
  }): Promise<boolean> => {
    const {
      requirement,
      web3Params: web3Params_2,
      stepIndex,
      flowActionType,
      flowTracking,
      isStaleSubmit: isStaleSubmit_2,
      isSupersededSubmit
    } = params_3;
    const {
      chain: chain_1,
      account: account_3
    } = web3Params_2;

    // Same reason as the executors below: a wallet prompt can resolve after a second checkout
    // replaced the tracking context, and this step's events belong to the one it started under.
    const checkoutTracking_0 = checkoutTrackingRef.current;
    const trackerParams = {
      chainId: chain_1.id,
      txSource: 'legacy',
      actionType: 'approval_tx',
      flowActionType: flowActionType ?? 'unknown',
      ...flowTracking,
      walletType: account_3.isSafe ? 'safe' : account_3.isContract ? 'smartWallet' : 'eoa',
      walletConnector: getWalletConnector(account_3),
      reviewSessionId: checkoutTracking_0?.reviewSessionId,
      attemptId: checkoutTracking_0?.attemptId
    } as const;
    setStatus(Status.signing);
    let gasTelemetry: GasTelemetry | undefined;
    let onchainGasLookup: OnchainGasLookup | undefined;
    let broadcastHash: Hash | undefined;
    try {
      const hash_1 = await sendAndConfirmTransaction({
        tx: requirement,
        web3Params: web3Params_2,
        isStaleSubmit: isStaleSubmit_2,
        onGasTelemetry: (telemetry_0, lookup) => {
          gasTelemetry = telemetry_0;
          onchainGasLookup = lookup;
        },
        onBroadcast: h => {
          broadcastHash = h;
        }
      });

      // A cancel/reset landed during gas estimation, before the approval was broadcast, or while
      // its Safe proposal was still queued:
      // report nothing and let the caller's staleness check end the flow quietly, the same way
      // the final-tx path returns undefined on that boundary.
      if (hash_1 === undefined) return false;
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Approval requirement completed',
        level: 'info',
        data: {
          txHash: hash_1,
          chainId: chain_1.id
        }
      });
      setStatus(Status.complete);
      trackTransactionOutcome({
        ...trackerParams,
        ...gasTelemetry,
        outcome: 'success',
        txHash: hash_1
      }, onchainGasLookup);
      maybeTrackStepResolved(checkoutTracking_0, {
        chainId: chain_1.id,
        walletType: trackerParams.walletType,
        stepType: 'approval',
        stepIndex,
        outcome: 'completed'
      });
      return true;
    } catch (error_2) {
      trackTransactionOutcome({
        ...trackerParams,
        ...gasTelemetry,
        outcome: 'failure',
        error: error_2,
        // EOA broadcast hashes are on-chain; contract wallets may still hold a safeTxHash.
        txHash: error_2 instanceof TransactionRevertedError ? error_2.hash : account_3.isContract ? undefined : broadcastHash
      }, onchainGasLookup);
      trackCheckoutStepFailure({
        tracking: checkoutTracking_0,
        error: error_2,
        chainId: chain_1.id,
        chainName: chain_1.name,
        walletType: getWalletType(account_3.isSafe, account_3.isContract),
        stepType: 'approval',
        stepIndex,
        rejectionStep: 'approval',
        operationTypes: ['approval_tx']
      });
      const message = logTransactionError(error_2, {
        chainId: chain_1.id,
        actionType: 'approval_tx',
        walletType: account_3.isSafe ? 'safe' : account_3.isContract ? 'smartWallet' : 'eoa'
      });
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Approval requirement failed',
        level: 'error',
        data: {
          chainId: chain_1.id,
          error: message
        }
      });
      if (isStaleSubmit_2?.()) {
        // A reset flow still owns the screen: a Safe proposal the wallet accepted after cancel
        // moved real money, so warn; only a newer submit silences it.
        if (error_2 instanceof WalletSendResultError && !isSupersededSubmit?.()) showErrorToast({
          error: error_2
        });
        return false;
      }
      if (error_2 instanceof WalletSendResultError) setRetryBlocked(true);
      if (error_2 instanceof SafeProposalPendingError) setSafeProposalMessage(error_2.message);
      showErrorToast({
        error: error_2
      });
      setStatus(Status.error);
      return false;
    }
  };

  /**
   * Executes an offchain signature requirement (e.g., EIP-712).
   */
  const executeSignatureRequirement = async (params_4: {
    requirement: SignatureRequirement;
    web3Params: Web3Params;
    stepIndex: number;
    flowActionType?: string;
    flowTracking: Pick<TrackTransactionParams, 'flow' | 'txFlag' | 'sdkAction'>;
  }): Promise<RequirementSignature | undefined> => {
    const {
      requirement: requirement_0,
      web3Params: web3Params_3,
      stepIndex: stepIndex_0,
      flowActionType: flowActionType_0,
      flowTracking: flowTracking_0
    } = params_4;
    const {
      client: client_1,
      account: account_4,
      chain: chain_2
    } = web3Params_3;
    setStatus(Status.signing);

    // See `executeApprovalRequirement`: the signature prompt can outlive its own checkout.
    const checkoutTracking_1 = checkoutTrackingRef.current;
    const trackerParams_0 = {
      chainId: chain_2.id,
      txSource: 'legacy',
      actionType: 'signature',
      flowActionType: flowActionType_0 ?? 'unknown',
      ...flowTracking_0,
      walletType: account_4.isSafe ? 'safe' : account_4.isContract ? 'smartWallet' : 'eoa',
      walletConnector: getWalletConnector(account_4),
      reviewSessionId: checkoutTracking_1?.reviewSessionId,
      attemptId: checkoutTracking_1?.attemptId
    } as const;
    try {
      const signed = await requirement_0.sign(client_1, account_4.address);
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Signature requirement completed',
        level: 'info',
        data: {
          chainId: chain_2.id
        }
      });
      trackTransaction({
        ...trackerParams_0,
        outcome: 'success'
      });
      maybeTrackStepResolved(checkoutTracking_1, {
        chainId: chain_2.id,
        walletType: trackerParams_0.walletType,
        stepType: 'signature',
        stepIndex: stepIndex_0,
        outcome: 'completed'
      });
      return signed;
    } catch (error_3) {
      trackTransaction({
        ...trackerParams_0,
        outcome: 'failure',
        error: error_3
      });
      trackCheckoutStepFailure({
        tracking: checkoutTracking_1,
        error: error_3,
        chainId: chain_2.id,
        chainName: chain_2.name,
        walletType: getWalletType(account_4.isSafe, account_4.isContract),
        stepType: 'signature',
        stepIndex: stepIndex_0,
        rejectionStep: 'signature',
        operationTypes: ['signature']
      });
      const message_0 = logTransactionError(error_3, {
        chainId: chain_2.id,
        actionType: 'signature',
        walletType: account_4.isSafe ? 'safe' : account_4.isContract ? 'smartWallet' : 'eoa'
      });
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Signature requirement failed',
        level: 'error',
        data: {
          chainId: chain_2.id,
          error: message_0
        }
      });
      showErrorToast({
        error: error_3
      });
      setStatus(Status.error);
      return undefined;
    }
  };

  /**
   * Keeps polling after the foreground deadline: one attempt per rejection until the wall-clock
   * deadline. Deliberately outlives a reset — see settleConfirmedReceipt for why a confirmation is
   * never dropped for staleness.
   */
  const waitForReceiptInBackground = async (waitForReceipt: (timeout: number) => Promise<ReceiptResult>): Promise<ReceiptResult | undefined> => {
    const deadline = Date.now() + TX_RECEIPT_BACKGROUND_TIMEOUT;
    for (;;) {
      const remaining = deadline - Date.now();
      if (remaining <= TX_RECEIPT_RETRY_DELAY) return undefined;
      try {
        return await waitForReceipt(remaining);
      } catch {
        await new Promise(resolve => setTimeout(resolve, TX_RECEIPT_RETRY_DELAY));
      }
    }
  };

  /**
   * Publishes a confirmed receipt into the flow's state for the Safe and final executors.
   *
   * Live submits are confirmed by the completed dialog. Stale or superseded submits still get a
   * success toast and refetch; the toast is their only confirmation that funds moved. Staleness
   * only gates writes to flow state that a `reset()` has already cleared.
   *
   * Returns the receipt hash (it may differ from the original if the tx was replaced —
   * defensive against React batching swallowing the onReplaced update in edge cases) plus
   * `isLive`, so the caller can gate its own final `setStatus(Status.complete)` after the
   * path-specific telemetry.
   */
  const settleConfirmedReceipt = (params_5: {
    receipt: ReceiptResult;
    fallbackHash: Hash;
    chainId: number;
    isContract: boolean;
    isStaleSubmit: () => boolean;
  }): {
    hash: Hash;
    isLive: boolean;
  } => {
    const {
      receipt: receipt_1,
      fallbackHash,
      chainId: chainId_0,
      isContract,
      isStaleSubmit: isStaleSubmit_3
    } = params_5;
    const isLive = !isStaleSubmit_3();
    const hash_2 = receipt_1.transactionHash ?? fallbackHash;
    if (isLive) setTxHash(hash_2);
    if (receipt_1.status !== 'success') {
      throw new TransactionRevertedError(hash_2);
    }
    if (isLive) {
      setReceipt(receipt_1);
      // A receipt that arrived during the background wait lifts the unconfirmed retry block.
      setRetryBlocked(false);
      setIsUnconfirmedTransaction(false);
    } else {
      displayTxToast({
        notificationStatus: NotificationStatus.success,
        txHash: hash_2,
        chainId: chainId_0,
        userIsContract: isContract
      });
    }
    return {
      hash: hash_2,
      isLive
    };
  };

  /**
   * Publishes an execution failure into the flow's state. Shared error epilogue of the Safe
   * and final-transaction executors.
   *
   * Same rule as the success path: always report the failure (the caller's telemetry runs
   * unconditionally), but only show it to the user while this flow is still the one that owns
   * the screen. A submit that was superseded would otherwise toast its own revert over the
   * flow that replaced it. And a reset cleared the flow's state, so a stale submit must not
   * write the error status a later submit now owns.
   */
  const settleFailedExecution = (params_6: {
    error: unknown;
    hash: Hash | undefined;
    chainId: number;
    isContract: boolean;
    requirements: readonly (BuiltTxRequirement | ActionRequirement)[];
    isStaleSubmit: () => boolean;
    isSupersededSubmit: () => boolean;
  }) => {
    const {
      error: error_4,
      hash: hash_3,
      chainId: chainId_1,
      isContract: isContract_0,
      requirements,
      isStaleSubmit: isStaleSubmit_4,
      isSupersededSubmit: isSupersededSubmit_0
    } = params_6;
    if (!isSupersededSubmit_0()) {
      showErrorToast({
        error: error_4,
        hash: hash_3,
        chainId: chainId_1,
        userIsContract: isContract_0
      });
    }
    if (!isStaleSubmit_4()) {
      if (error_4 instanceof WalletSendResultError) setRetryBlocked(true);
      if (error_4 instanceof SafeProposalPendingError) setSafeProposalMessage(error_4.message);
      // The receipt came back after the poll had given up: the outcome is known again.
      if (error_4 instanceof TransactionRevertedError) {
        setRetryBlocked(false);
        setIsUnconfirmedTransaction(false);
      }
      // A broadcast tx whose receipt never came back may still confirm: resubmitting would send
      // the same bundle (and the same Permit2 nonce) a second time. Only a confirmed revert is a
      // known outcome that retry can safely repeat.
      if (hash_3 && !(error_4 instanceof TransactionRevertedError)) {
        setRetryBlocked(true);
        setIsUnconfirmedTransaction(true);
      }
      if (!hash_3) setRetryDisabled(true);
      // Re-sign from the first signature step; the deadline stays so an expired one keeps retry disabled.
      if (error_4 instanceof SimulationError && error_4.revertCategory === 'invalid_signature') {
        const firstSignatureStep = requirements.findIndex(isRequirementSignature);
        if (firstSignatureStep >= 0) {
          cachedSignatureRequirements.current = [];
          setStep(firstSignatureStep);
        }
      } else if (error_4 instanceof SimulationError && requirements.some(isRequirementSignature)) {
        const now = Math.floor(Date.now() / 1000);
        if (error_4.revertCategory === 'signature_expired') {
          // Expired signatures need a full rebuild; expiring the deadline disables retry and shows the alert.
          setSignatureDeadline(prev => Math.min(prev ?? now, now));
        } else if (error_4.revertCategory === 'stale_nonce') {
          setRetryBlocked(true);
          setIsStaleNonceError(true);
        }
      }
      setIsAcceptableSimulationError(error_4 instanceof SimulationError && error_4.isAcceptable);
      const isLimitViolation = error_4 instanceof SimulationError && error_4.details?.code === 'CONSUMER_LIMIT_VIOLATION';
      // Retry resends the same quote; only a rebuild from fresh data can pass the limit again.
      if (isLimitViolation) setRetryBlocked(true);
      setIsLimitViolationError(isLimitViolation);
      setIsUnverifiedSimulationError(error_4 instanceof SimulationError && UNVERIFIED_SIMULATION_CODES.has(error_4.details?.code ?? ''));
      setStatus(Status.error);
    }
  };

  /**
   * Executes transactions through Safe wallet (batched).
   */
  const executeSafeTransactions = async (params_7: {
    approvalTxs: Array<BuiltTxRequirement>;
    requirements: readonly (BuiltTxRequirement | ActionRequirement)[];
    transaction: TransactionBuilder;
    web3Params: Web3Params;
    actionType?: string;
    flowTracking: Pick<TrackTransactionParams, 'flow' | 'txFlag' | 'sdkAction'>;
    /** Vault withdraw-family exit derived upstream from the SDK action; absent on other operations */
    exitType?: VaultExitType;
    skipSimulation?: boolean;
    requiresSuccessfulSimulation?: boolean;
    isStaleSubmit: () => boolean;
    isSupersededSubmit: () => boolean;
  }) => {
    const {
      approvalTxs,
      requirements: requirements_0,
      transaction,
      web3Params: web3Params_4,
      actionType,
      flowTracking: flowTracking_1,
      exitType,
      skipSimulation,
      requiresSuccessfulSimulation,
      isStaleSubmit: isStaleSubmit_5,
      isSupersededSubmit: isSupersededSubmit_1
    } = params_7;
    const {
      client: client_2,
      account: account_5,
      chain: chain_3
    } = web3Params_4;

    // Snapshotted, not read late: `checkoutTrackingRef` is overwritten the moment a second
    // checkout starts, and a broadcast submit outlives that — its receipt would otherwise
    // resolve the *new* session's funnel step before the new transaction had confirmed.
    // The tracker params below already froze it at this point; the rest of the executor now
    // reads the same snapshot instead of whatever owns the screen when the receipt lands.
    const checkoutTracking_2 = checkoutTrackingRef.current;
    const trackerParams_1 = {
      chainId: chain_3.id,
      txSource: 'legacy',
      actionType,
      ...flowTracking_1,
      walletType: 'safe',
      walletConnector: getWalletConnector(account_5),
      // Safe batches approvals + final through this executor and never runs `applyGasBuffer`, so
      // its wallet-managed source is stamped here. Without it every Safe `tx_outcome` omits
      // `gas_limit_source` and the wallet cohort on the buffer dashboard is undercounted.
      gasLimitSource: 'wallet',
      exitType,
      reviewSessionId: checkoutTracking_2?.reviewSessionId,
      attemptId: checkoutTracking_2?.attemptId
    } as const;
    setStep('final');
    setStatus(Status.signing);
    const tx_2 = transaction.buildTx();
    const txs = [...approvalTxs, tx_2].map(t => ({
      from: account_5.address,
      to: t.to,
      value: t.value.toString(),
      data: t.data
    }));
    let hash_4: Hash | undefined;
    const trackFailure = (error_5: unknown) => {
      trackTransaction({
        ...trackerParams_1,
        outcome: 'failure',
        error: error_5,
        // `hash` still holds a safeTxHash for unexecuted proposals; only a mined revert
        // is an on-chain hash worth reporting.
        txHash: error_5 instanceof TransactionRevertedError ? error_5.hash : undefined
      });
      trackTransactionFailed({
        txHash: hash_4,
        chainId: chain_3.id,
        chainName: chain_3.name,
        operationTypes: [actionType ?? 'safe_batch'],
        walletType: 'safe',
        error: error_5,
        exitType
      });
      trackCheckoutStepFailure({
        tracking: checkoutTracking_2,
        error: error_5,
        chainId: chain_3.id,
        chainName: chain_3.name,
        walletType: 'safe',
        stepType: 'final',
        stepIndex: checkoutTracking_2?.totalSteps ?? 1,
        rejectionStep: 'safe_batch',
        operationTypes: [actionType ?? 'safe_batch']
      });
      const message_1 = logTransactionError(error_5, {
        chainId: chain_3.id,
        actionType,
        walletType: 'safe',
        txHash: hash_4
      });
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Safe transaction failed',
        level: 'error',
        data: {
          txHash: hash_4,
          chainId: chain_3.id,
          error: message_1,
          simulationBypassed: skipSimulation ?? false
        }
      });
    };
    const settleFailure = (error_6: unknown) => settleFailedExecution({
      error: error_6,
      hash: hash_4,
      chainId: chain_3.id,
      isContract: account_5.isContract,
      requirements: requirements_0,
      isStaleSubmit: isStaleSubmit_5,
      isSupersededSubmit: isSupersededSubmit_1
    });
    const reportFailure = (error_7: unknown) => {
      trackFailure(error_7);
      settleFailure(error_7);
    };
    try {
      if ((isSimulationEnabled || requiresSuccessfulSimulation) && !skipSimulation && (
      // A required simulation still runs (and fails closed) for an unsupported sender.
      requiresSuccessfulSimulation || !isSimulationUnsupportedSender(chain_3.id, account_5))) {
        safeSentryBreadcrumb({
          category: 'simulation',
          message: 'Starting simulation',
          level: 'info',
          data: {
            chainId: chain_3.id,
            txCount: txs.length,
            actionType
          }
        });
        await checkSimulation(chain_3.id, txs, {
          limits: buildSimulationLimits(tx_2.action),
          ...getSimulationTrackingOptions(checkoutTracking_2)
        });
      }

      // A cancel/reset during the simulation await bumps the submit generation; bail before
      // proposing the funds-moving Safe transaction so an abandoned flow can't reach the wallet.
      if (isStaleSubmit_5()) return undefined;
      const sdk = new SafeAppsSDK();
      const {
        safeTxHash
      } = await sdk.txs.send({
        txs
      });
      // Safe connector converts safeTxHash to submitted txHash when waiting
      if (!isHash(safeTxHash)) throw new WalletSendResultError(safeTxHash);
      hash_4 = safeTxHash;
      setTxHash(hash_4);
      setStatus(Status.pending);
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Safe transaction signed',
        level: 'info',
        data: {
          txHash: hash_4,
          chainId: chain_3.id,
          transactionCount: txs.length,
          simulationBypassed: skipSimulation ?? false
        }
      });
      trackTransactionSigned({
        txHash: hash_4,
        chainId: chain_3.id,
        chainName: chain_3.name,
        walletType: 'safe',
        transactionCount: txs.length,
        simulationBypassed: skipSimulation ?? false,
        checkoutTracking: checkoutTracking_2,
        exitType
      });
      await Time.wait(TX_INDEX_DELAY);
      const safeReceiptClient = getReceiptPollingClient({
        wagmiConfig,
        walletClient: client_2,
        chainId: chain_3.id,
        isSafe: true
      });
      // `hash` is read inside the closure so the background wait sees a replacement hash.
      const waitForReceipt_0 = (timeout: number) => waitForReceiptWithReplacementDetection({
        pollingClient: safeReceiptClient,
        walletClient: client_2,
        hash: hash_4 as Hash,
        senderAddress: account_5.address,
        retryDelay: TX_RECEIPT_RETRY_DELAY,
        timeout,
        onReplaced: replacement_0 => {
          const prevHash = hash_4;
          hash_4 = replacement_0.transaction.hash;
          // Same reason as the post-await guard below: an abandoned poll must not
          // publish its replacement hash over the flow that superseded it.
          if (!isStaleSubmit_5()) setTxHash(hash_4);
          safeSentryBreadcrumb({
            category: 'transaction',
            message: `Safe transaction ${replacement_0.reason}`,
            level: 'warning',
            data: {
              originalHash: prevHash,
              newHash: hash_4,
              chainId: chain_3.id,
              reason: replacement_0.reason
            }
          });
        }
      });
      let receipt_2: ReceiptResult;
      try {
        receipt_2 = await waitForReceipt_0(TX_RECEIPT_TIMEOUT);
      } catch (error_9) {
        // Keep the current unconfirmed UI state while watching for a receipt in the background.
        settleFailure(error_9);
        trackTransaction({
          ...trackerParams_1,
          outcome: 'receipt_timeout_pending'
        });
        safeSentryBreadcrumb({
          category: 'transaction',
          message: 'Safe receipt poll timed out, watching in background',
          level: 'warning',
          data: {
            txHash: hash_4,
            chainId: chain_3.id
          }
        });
        const lateReceipt = await waitForReceiptInBackground(waitForReceipt_0);
        if (!lateReceipt) {
          trackFailure(error_9);
          return undefined;
        }
        receipt_2 = lateReceipt;
      }
      const settled = settleConfirmedReceipt({
        receipt: receipt_2,
        fallbackHash: hash_4,
        chainId: chain_3.id,
        isContract: account_5.isContract,
        isStaleSubmit: isStaleSubmit_5
      });
      hash_4 = settled.hash;
      const isLive_0 = settled.isLive;
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Safe transaction confirmed',
        level: 'info',
        data: {
          txHash: receipt_2.transactionHash,
          chainId: chain_3.id,
          blockNumber: receipt_2.blockNumber.toString()
        }
      });
      trackTransactionConfirmed({
        txHash: receipt_2.transactionHash,
        chainId: chain_3.id,
        chainName: chain_3.name,
        blockNumber: receipt_2.blockNumber.toString(),
        operationTypes: [actionType ?? 'safe_batch'],
        walletType: 'safe',
        exitType
      });
      maybeTrackStepResolved(checkoutTracking_2, {
        chainId: chain_3.id,
        walletType: 'safe',
        stepType: 'final',
        stepIndex: checkoutTracking_2?.totalSteps ?? 1,
        outcome: 'completed'
      });
      if (isLive_0) setStatus(Status.complete);
      trackTransaction({
        ...trackerParams_1,
        outcome: 'success',
        txHash: receipt_2.transactionHash
      });
      // Returned unconditionally — this is what lets the caller run `onTxSuccess`, so a
      // confirmed transaction always refetches the balance it just changed.
      return {
        hash: hash_4,
        receipt: receipt_2
      };
    } catch (error_8) {
      reportFailure(error_8);
    }
  };

  /**
   * Executes the final transaction after all requirements are met.
   */
  const executeFinalTransaction = async (params_8: {
    requirements: readonly (BuiltTxRequirement | ActionRequirement)[];
    transaction: TransactionBuilder;
    signatures: Array<RequirementSignature | undefined>;
    web3Params: Web3Params;
    actionType?: string;
    flowTracking: Pick<TrackTransactionParams, 'flow' | 'txFlag' | 'sdkAction'>;
    /** Vault withdraw-family exit derived upstream from the SDK action; absent on other operations */
    exitType?: VaultExitType;
    skipSimulation?: boolean;
    requiresSuccessfulSimulation?: boolean;
    isStaleSubmit: () => boolean;
    isSupersededSubmit: () => boolean;
  }) => {
    const {
      requirements: requirements_1,
      transaction: transaction_0,
      signatures,
      web3Params: web3Params_5,
      actionType: actionType_0,
      flowTracking: flowTracking_2,
      exitType: exitType_0,
      skipSimulation: skipSimulation_0,
      requiresSuccessfulSimulation: requiresSuccessfulSimulation_0,
      isStaleSubmit: isStaleSubmit_6,
      isSupersededSubmit: isSupersededSubmit_2
    } = params_8;
    const {
      client: client_3,
      account: account_6,
      chain: chain_4
    } = web3Params_5;

    // Snapshotted, not read late: `checkoutTrackingRef` is overwritten the moment a second
    // checkout starts, and a broadcast submit outlives that — its receipt would otherwise
    // resolve the *new* session's funnel step before the new transaction had confirmed.
    // The tracker params below already froze it at this point; the rest of the executor now
    // reads the same snapshot instead of whatever owns the screen when the receipt lands.
    const checkoutTracking_3 = checkoutTrackingRef.current;
    const trackerParams_2 = {
      chainId: chain_4.id,
      txSource: 'legacy',
      actionType: actionType_0,
      ...flowTracking_2,
      walletType: account_6.isSafe ? 'safe' : account_6.isContract ? 'smartWallet' : 'eoa',
      walletConnector: getWalletConnector(account_6),
      exitType: exitType_0,
      reviewSessionId: checkoutTracking_3?.reviewSessionId,
      attemptId: checkoutTracking_3?.attemptId
    } as const;
    setStep('final');
    setStatus(Status.signing);
    let hash_5: Hash | undefined;
    const walletType = getWalletType(account_6.isSafe, account_6.isContract);
    let gasTelemetry_0: GasTelemetry | undefined;
    let onchainGasLookup_0: OnchainGasLookup | undefined;
    const trackFailure_0 = (error_10: unknown) => {
      trackTransactionOutcome({
        ...trackerParams_2,
        ...gasTelemetry_0,
        outcome: 'failure',
        error: error_10,
        // EOA broadcast hashes are on-chain; contract wallets may still hold a safeTxHash.
        txHash: error_10 instanceof TransactionRevertedError ? error_10.hash : account_6.isContract ? undefined : hash_5
      }, onchainGasLookup_0);
      trackTransactionFailed({
        txHash: hash_5,
        chainId: chain_4.id,
        chainName: chain_4.name,
        operationTypes: [actionType_0 ?? 'unknown'],
        walletType,
        error: error_10,
        exitType: exitType_0
      });
      trackCheckoutStepFailure({
        tracking: checkoutTracking_3,
        error: error_10,
        chainId: chain_4.id,
        chainName: chain_4.name,
        walletType,
        stepType: 'final',
        stepIndex: checkoutTracking_3?.totalSteps ?? 1,
        rejectionStep: 'final',
        operationTypes: [actionType_0 ?? 'unknown']
      });
      const message_2 = logTransactionError(error_10, {
        chainId: chain_4.id,
        actionType: actionType_0,
        walletType: account_6.isSafe ? 'safe' : account_6.isContract ? 'smartWallet' : 'eoa',
        txHash: hash_5
      });
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Final transaction failed',
        level: 'error',
        data: {
          txHash: hash_5,
          chainId: chain_4.id,
          error: message_2,
          simulationBypassed: skipSimulation_0 ?? false
        }
      });
    };
    const settleFailure_0 = (error_11: unknown) => settleFailedExecution({
      error: error_11,
      hash: hash_5,
      chainId: chain_4.id,
      isContract: account_6.isContract,
      requirements: requirements_1,
      isStaleSubmit: isStaleSubmit_6,
      isSupersededSubmit: isSupersededSubmit_2
    });
    const reportFailure_0 = (error_12: unknown) => {
      trackFailure_0(error_12);
      settleFailure_0(error_12);
    };
    try {
      // Pass every collected signature (permit + off-chain setAuthorization) to the v5
      // builder; `buildTx` splits them via `selectRequirementSignatures` and folds the
      // authorization one into `setAuthorizationWithSig`.
      // Runs inside the try so `BundlesPermitMismatchError` surfaces through the transaction error path.
      const tx_3 = transaction_0.buildTx(signatures.filter((signature): signature is RequirementSignature => signature != null));
      // On this path every approval/authorization requirement has already been mined by
      // `processRequirements`; the simulation waits for its backend to reach that block.
      // Simulate only the final tx: replaying a mined `approve` reverts on approve-only-once
      // tokens (USDT-style non-zero -> non-zero guard) and would fail a valid deposit.
      const txs_0 = [{
        from: account_6.address,
        to: tx_3.to,
        value: tx_3.value.toString(),
        data: tx_3.data
      }];
      if ((isSimulationEnabled || requiresSuccessfulSimulation_0) && !skipSimulation_0 && (
      // A required simulation still runs (and fails closed) for an unsupported sender.
      requiresSuccessfulSimulation_0 || !isSimulationUnsupportedSender(chain_4.id, account_6))) {
        safeSentryBreadcrumb({
          category: 'simulation',
          message: 'Starting simulation',
          level: 'info',
          data: {
            chainId: chain_4.id,
            txCount: txs_0.length,
            actionType: actionType_0
          }
        });
        await checkSimulation(chain_4.id, txs_0, {
          limits: buildSimulationLimits(tx_3.action),
          minBlockNumber: minedRequirementBlock.current,
          requirementTxHashes: minedRequirementTxHashes.current,
          ...getSimulationTrackingOptions(checkoutTracking_3)
        });
      }

      // A cancel/reset during the simulation await bumps the submit generation; bail before
      // the funds-moving send so an abandoned flow can't still prompt the wallet.
      if (isStaleSubmit_6()) return undefined;
      const {
        tx: txWithGasBuffer_0,
        telemetry: telemetry_1
      } = await applyGasBuffer(tx_3, web3Params_5);
      gasTelemetry_0 = telemetry_1;
      // Estimation is another async boundary; cancellation must not resume into the
      // funds-moving wallet prompt.
      if (isStaleSubmit_6()) return undefined;
      const sendResult_0: unknown = await sendWalletTransaction(client_3, {
        ...txWithGasBuffer_0,
        account: account_6.address,
        chain: chain_4
      }, isStaleSubmit_6);
      hash_5 = await resolveWalletSendResult({
        result: sendResult_0,
        tx: txWithGasBuffer_0,
        web3Params: web3Params_5,
        step: 'final',
        isStaleSubmit: isStaleSubmit_6
      });
      if (!hash_5) return undefined;
      setTxHash(hash_5);
      setStatus(Status.pending);
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Final transaction signed',
        level: 'info',
        data: {
          txHash: hash_5,
          chainId: chain_4.id,
          isSafe: account_6.isSafe,
          simulationBypassed: skipSimulation_0 ?? false
        }
      });
      trackTransactionSigned({
        txHash: hash_5,
        chainId: chain_4.id,
        chainName: chain_4.name,
        walletType,
        transactionCount: txs_0.length,
        simulationBypassed: skipSimulation_0 ?? false,
        checkoutTracking: checkoutTracking_3,
        exitType: exitType_0
      });
      await Time.wait(TX_INDEX_DELAY);
      const finalReceiptClient = getReceiptPollingClient({
        wagmiConfig,
        walletClient: client_3,
        chainId: chain_4.id,
        isSafe: account_6.isSafe
      });
      // A repriced (sped-up) tx runs the same calldata, so its gasUsed still measures the buffer;
      // a cancel/replace runs different calldata, so its gasUsed is flagged out of the stats.
      let gasReceiptReplaced_0 = false;
      // `hash` is read inside the closure so the background wait sees a replacement hash.
      const waitForReceipt_1 = (timeout_0: number) => waitForReceiptWithReplacementDetection({
        pollingClient: finalReceiptClient,
        walletClient: client_3,
        hash: hash_5 as Hash,
        senderAddress: account_6.address,
        retryDelay: TX_RECEIPT_RETRY_DELAY,
        timeout: timeout_0,
        onReplaced: replacement_1 => {
          if (replacement_1.reason !== 'repriced') gasReceiptReplaced_0 = true;
          const prevHash_0 = hash_5;
          hash_5 = replacement_1.transaction.hash;
          // Same reason as the post-await guard below: an abandoned poll must not
          // publish its replacement hash over the flow that superseded it.
          if (!isStaleSubmit_6()) setTxHash(hash_5);
          safeSentryBreadcrumb({
            category: 'transaction',
            message: `Final transaction ${replacement_1.reason}`,
            level: 'warning',
            data: {
              originalHash: prevHash_0,
              newHash: hash_5,
              chainId: chain_4.id,
              actionType: actionType_0,
              reason: replacement_1.reason
            }
          });
        }
      });
      let receipt_3: ReceiptResult;
      try {
        receipt_3 = await waitForReceipt_1(TX_RECEIPT_TIMEOUT);
      } catch (error_14) {
        // Keep the current unconfirmed UI state while watching for a receipt in the background.
        settleFailure_0(error_14);
        trackTransaction({
          ...trackerParams_2,
          ...gasTelemetry_0,
          outcome: 'receipt_timeout_pending',
          // Contract wallets may still hold an unresolved safeTxHash here.
          txHash: account_6.isContract ? undefined : hash_5
        });
        safeSentryBreadcrumb({
          category: 'transaction',
          message: 'Final transaction receipt poll timed out, watching in background',
          level: 'warning',
          data: {
            txHash: hash_5,
            chainId: chain_4.id
          }
        });
        const lateReceipt_0 = await waitForReceiptInBackground(waitForReceipt_1);
        if (!lateReceipt_0) {
          trackFailure_0(error_14);
          return undefined;
        }
        receipt_3 = lateReceipt_0;
      }
      gasTelemetry_0 = {
        ...gasTelemetry_0,
        gasUsed: Number(receipt_3.gasUsed),
        gasReceiptReplaced: gasReceiptReplaced_0
      };
      onchainGasLookup_0 = account_6.isSafe || gasReceiptReplaced_0 ? undefined : {
        client: finalReceiptClient,
        hash: receipt_3.transactionHash,
        from: account_6.address
      };
      const settled_0 = settleConfirmedReceipt({
        receipt: receipt_3,
        fallbackHash: hash_5,
        chainId: chain_4.id,
        isContract: account_6.isContract,
        isStaleSubmit: isStaleSubmit_6
      });
      hash_5 = settled_0.hash;
      const isLive_1 = settled_0.isLive;
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Final transaction confirmed',
        level: 'info',
        data: {
          txHash: receipt_3.transactionHash,
          chainId: chain_4.id,
          blockNumber: receipt_3.blockNumber.toString()
        }
      });
      trackTransactionConfirmed({
        txHash: receipt_3.transactionHash,
        chainId: chain_4.id,
        chainName: chain_4.name,
        blockNumber: receipt_3.blockNumber.toString(),
        operationTypes: [actionType_0 ?? 'unknown'],
        walletType,
        exitType: exitType_0
      });
      maybeTrackStepResolved(checkoutTracking_3, {
        chainId: chain_4.id,
        walletType,
        stepType: 'final',
        stepIndex: checkoutTracking_3?.totalSteps ?? 1,
        outcome: 'completed'
      });
      if (isLive_1) setStatus(Status.complete);
      trackTransactionOutcome({
        ...trackerParams_2,
        ...gasTelemetry_0,
        outcome: 'success',
        txHash: receipt_3.transactionHash
      }, onchainGasLookup_0);
      // Returned unconditionally — this is what lets the caller run `onTxSuccess`, so a
      // confirmed transaction always refetches the balance it just changed.
      return {
        hash: hash_5,
        receipt: receipt_3
      };
    } catch (error_13) {
      reportFailure_0(error_13);
    }
  };

  /**
   * Processes all requirements (signatures and approvals) in sequence.
   * @returns Updated signature requirements array, or undefined if a step failed
   */
  const processRequirements = async (params_9: ProcessRequirementsParams): Promise<RequirementSignature[] | undefined> => {
    const {
      requirements: requirements_2,
      startStep,
      web3Params: web3Params_6,
      flowActionType: flowActionType_1,
      flowTracking: flowTracking_3,
      isStaleSubmit: isStaleSubmit_7,
      isSupersededSubmit: isSupersededSubmit_3
    } = params_9;
    const updatedSignatures = [...cachedSignatureRequirements.current];
    for (let i = startStep; i < requirements_2.length; i++) {
      setStep(i);
      const requirement_1 = requirements_2[i];
      if (isRequirementSignature(requirement_1)) {
        const signed_0 = await executeSignatureRequirement({
          requirement: requirement_1,
          web3Params: web3Params_6,
          stepIndex: i + 1,
          flowActionType: flowActionType_1,
          flowTracking: flowTracking_3
        });
        if (!signed_0) return undefined;
        // The prompt can resolve after a cancel (reset() bumps the generation); bail before caching.
        if (isStaleSubmit_7?.()) return undefined;
        updatedSignatures.push(signed_0);
        cachedSignatureRequirements.current = [...updatedSignatures];

        // Track the earliest signature deadline (Midnight offer-root signatures carry none)
        if ('deadline' in signed_0.args) {
          const deadline_0 = Number(signed_0.args.deadline);
          setSignatureDeadline(prev_0 => !prev_0 ? deadline_0 : Math.min(prev_0, deadline_0));
        }
      }

      // Approval and Morpho setAuthorization are both built txs sent the same way
      // (only signatures are signed). Authorization appears for morpho-sdk borrow /
      // withdraw-collateral / combos so GeneralAdapter1 can act on the user's behalf.
      if (isRequirementApproval(requirement_1) || isRequirementBlueAuthorization(requirement_1)) {
        if (completedOnChainSteps.current.has(i)) continue;
        const isApproved = await executeApprovalRequirement({
          requirement: requirement_1,
          web3Params: web3Params_6,
          stepIndex: i + 1,
          flowActionType: flowActionType_1,
          flowTracking: flowTracking_3,
          isStaleSubmit: isStaleSubmit_7,
          isSupersededSubmit: isSupersededSubmit_3
        });
        if (!isApproved) return undefined;
        // Same guard after the approval/authorization await.
        if (isStaleSubmit_7?.()) return undefined;
        completedOnChainSteps.current.add(i);
      }
    }
    return updatedSignatures;
  };
  const isInvalidResumeStep = (startStep_0: number, requirementCount: number) => startStep_0 < 0 || requirementCount > 0 && startStep_0 >= requirementCount;
  const submitTransactions = async (client_4: WalletClient, params_10: InternalExecuteParams) => {
    const {
      transaction: transaction_1,
      requirements: requirements_3 = [],
      startStep: startStep_1 = 0,
      isRetry = false,
      chain: chain_5,
      account: account_7,
      onTxSuccess,
      actionType: actionType_1,
      flow,
      sdkAction,
      skipSimulation: skipSimulation_1,
      requiresSuccessfulSimulation: requiresSuccessfulSimulation_1,
      skipFinalSimulation,
      checkoutTracking: checkoutTracking_4
    } = params_10;
    const flowTracking_4 = {
      ...getFlowTracking(flags, flow),
      sdkAction
    };

    // A safety-critical bundle can never inherit the generic "Proceed anyway" retry policy.
    const effectiveSkipSimulation = requiresSuccessfulSimulation_1 ? false : skipSimulation_1;
    const effectiveSkipFinalSimulation = effectiveSkipSimulation || !requiresSuccessfulSimulation_1 && !!skipFinalSimulation;
    const web3Params_7 = validateClientParams({
      client: client_4,
      chain: chain_5,
      account: account_7
    });
    try {
      assertContractWalletRequirements(account_7, requirements_3);
    } catch (error_15) {
      showErrorToast({
        error: error_15
      });
      throw error_15;
    }

    // Generation token for this submit. reset()/a newer submit bumps the ref; we re-check after
    // EVERY awaited boundary (screening, each requirement, before the final tx) so a wallet
    // prompt resolved after a cancel can never advance the flow.
    const submitGeneration = ++submitGenerationRef.current;
    const isStaleSubmit_8 = () => submitGenerationRef.current !== submitGeneration;
    const submitSequence = ++submitSequenceRef.current;
    const isSupersededSubmit_4 = () => submitSequenceRef.current !== submitSequence;

    // A confirmed transaction always has to refresh the data it changed, or the user is left
    // staring at a stale balance — the state that invites them to pay for the same thing twice.
    // But `onTxSuccess` is an arbitrary consumer callback and several of them do more than
    // refetch: they close the global dialog, or clear the amount input. Running one for a flow
    // the user has already left would reach into whatever transaction owns the screen now. So a
    // live flow runs its own callback, and a stale one gets the refresh without the UI.
    // The stale path invalidates every query rather than the ones this transaction touched: the
    // flow that knew which those were is the one that is gone. It is a rare path, and a refetch
    // too many is cheaper than a balance left stale.
    const refreshAfterSuccess = (receipt_4: ReceiptResult) => {
      if (isStaleSubmit_8()) {
        void queryClient.invalidateQueries();
        return;
      }
      onTxSuccess?.(receipt_4);
    };
    safeSentryBreadcrumb({
      category: 'transaction',
      message: 'Transaction V2 bundle initiated',
      level: 'info',
      data: {
        chainId: chain_5.id,
        requirementCount: requirements_3.length,
        requirementKinds: requirements_3.map(describeRequirement).join(','),
        isSafe: account_7.isSafe,
        isRetry
      }
    });

    // Read once per submit so the value also reaches the executors on a retry.
    const exitType_1 = resolveVaultExitType(transaction_1, actionType_1);
    setChain(chain_5);
    setAccount(account_7);
    checkoutTrackingRef.current = checkoutTracking_4;
    setCheckoutTracking(checkoutTracking_4);
    setActiveTransactions({
      requirements: requirements_3,
      transaction: transaction_1,
      actionType: actionType_1,
      flow,
      sdkAction,
      requiresSuccessfulSimulation: requiresSuccessfulSimulation_1,
      skipFinalSimulation,
      onTxSuccess
    });

    // Fresh entry only — NOT a retry, even one resuming at step 0 (a first-requirement failure).
    // Keying off `isRetry` rather than `startStep === 0` keeps the once-on-entry screen from
    // re-running on a first-step retry, where a transient outage could otherwise block recovery.
    if (!isRetry) {
      // Fresh submit: drop signatures/deadline cached by a previous flow — a stale permit
      // reaching this bundle's buildTx would throw AmbiguousRequirementSignaturesError.
      cachedSignatureRequirements.current = [];
      completedOnChainSteps.current = new Set();
      minedRequirementBlock.current = undefined;
      minedRequirementTxHashes.current = [];
      setSignatureDeadline(undefined);
      setRetryBlocked(false);
      setIsStaleNonceError(false);
      setIsLimitViolationError(false);
      setIsUnverifiedSimulationError(false);
      setIsUnconfirmedTransaction(false);
      setSafeProposalMessage(undefined);
      setIsAwaitingSafeExecution(false);
      setIsSafeStatusUnavailable(false);
      setScreeningError(undefined);
      // Clear any terminal presentation left by a prior flow that closed via toggleDialog(false)
      // without reset(): otherwise, while the screening verdict is in flight, the dialog renders
      // this new submit as already complete (Done button + the previous tx hash).
      setStatus(undefined);
      setTxHash(undefined);
      setReceipt(undefined);

      // Enforcement layer behind the connect-time disconnect, run once as the user ENTERS the
      // transaction flow — before any wallet prompt, and only here (not on retries or before the
      // final tx). Screens FRESH (staleTime 0, never a cached verdict) so a connection that
      // survived or was re-added for a screened-out wallet (APPS-1315) is caught, and fails closed:
      // a screening outage blocks rather than waving the tx through. isScreening drives the dialog's
      // verifying state and attributes a cancel during the await to the 'screening' step.
      setIsScreening(true);
      // The Vaults app's E2E build swaps in a mock wallet keyed off NEXT_PUBLIC_FALLBACK_FLAGS_ENVIRONMENT,
      // not the shared NEXT_PUBLIC_E2E_TEST that ensureAuthorizedUser waives on, and CI wires
      // neither Chainalysis credentials nor a mainnet RPC. Waive the gate on the app's own E2E
      // signal too, or every E2E transaction would hit the live route and fail closed.
      const verdict: ScreeningVerdict = clientEnvs.NEXT_PUBLIC_FALLBACK_FLAGS_ENVIRONMENT === FeatureFlagEnvironment.E2E ? {
        authorized: true
      } : await ensureAuthorizedUser(queryClient, web3Params_7.account.address, {
        screenAddressEndpoint: SCREEN_ADDRESS_ENDPOINT,
        staleTime: 0
      });

      // The processing dialog is opened non-closable, so an in-flight verdict cannot resume
      // behind a dismissed dialog.
      // A cancel/reset while screening was in flight bumped the generation; bail so an
      // authorized verdict can't resume into wallet prompts. Clear the verifying flag only when
      // this submit still owns the current generation — a superseded submit resolving late must
      // not drop the verifying state that the newer submit set for itself.
      if (isStaleSubmit_8()) return;
      setIsScreening(false);
      if (!verdict.authorized) {
        // 'blocked' is a definitive screened-out verdict (terminal). 'unavailable' means the screen
        // never returned one — an outage, the tx-time deadline, or a forced-fresh screen reverted by
        // a wallet switch mid-flight; it still fails closed here, but the copy must read as a
        // transient, retryable service state rather than accusing the wallet (retry affordance is
        // keyed off this message in ConfirmDialogV2).
        setStep(0);
        setStatus(undefined);
        setScreeningError(verdict.reason === 'unavailable' ? SCREENING_UNAVAILABLE_MESSAGE : SCREENING_BLOCKED_MESSAGE);
        safeSentryBreadcrumb({
          category: 'transaction',
          message: 'Transaction blocked by address screening',
          level: 'warning',
          // Verdict reason (blocked vs unavailable) is safe to log; the Chainalysis risk category
          // that drove a block stays server-side, never sent to the client.
          data: {
            chainId: chain_5.id,
            reason: verdict.reason
          }
        });
        // Record a terminal funnel event so the confirmed attempt does not dangle with no
        // outcome (the review dialog already emitted review_confirmed).
        if (checkoutTrackingRef.current) {
          trackTransactionFlowCancelled({
            tracking: checkoutTrackingRef.current,
            chainId: chain_5.id,
            walletType: getWalletType(account_7.isSafe, account_7.isContract),
            step: 'screening'
          });
        }
        return;
      }

      // Only advance into the signing UI once the signer is cleared, so ConfirmDialogV2 never
      // tells the user to "Proceed in your wallet" while screening is still in flight.
      setStep(account_7.isSafe || requirements_3.length === 0 ? 'final' : 0);
      setStatus(Status.signing);
      trackTransactionInitiated({
        chainId: chain_5.id,
        chainName: chain_5.name,
        operationTypes: [actionType_1 ?? 'unknown'],
        isSimpleTx: false,
        walletType: getWalletType(account_7.isSafe, account_7.isContract),
        exitType: exitType_1
      });
    }

    // Safe wallet flow: batch all transactions
    if (account_7.isSafe) {
      // Batch both built-tx requirement kinds (approval + setAuthorization) ahead of the final tx.
      const approvalTxs_0 = requirements_3.filter((requirement_2): requirement_2 is BuiltTxRequirement => isRequirementApproval(requirement_2) || isRequirementBlueAuthorization(requirement_2));
      // The Safe batch only carries built txs and calls `buildTx()` unsigned: a signature
      // Requirement would be silently dropped and the final tx encoded without it (or as a
      // placeholder — see getWrapMorphoV2Transaction). No flow builds one for a Safe today
      // (`supportsSignatures` excludes contract wallets) — fail loudly if that ever breaks
      // instead of proposing a broken batch.
      if (requirements_3.some(isRequirementSignature)) {
        throw new Error('Safe flow received a signature requirement; the batch would drop it');
      }
      const result_0 = await executeSafeTransactions({
        approvalTxs: approvalTxs_0,
        requirements: requirements_3,
        transaction: transaction_1,
        web3Params: web3Params_7,
        actionType: actionType_1,
        flowTracking: flowTracking_4,
        exitType: exitType_1,
        skipSimulation: effectiveSkipSimulation,
        requiresSuccessfulSimulation: requiresSuccessfulSimulation_1,
        isStaleSubmit: isStaleSubmit_8,
        isSupersededSubmit: isSupersededSubmit_4
      });
      if (!result_0) return;
      // A confirmed Safe proposal refreshes what it changed, exactly as the EOA path below does.
      refreshAfterSuccess(result_0.receipt);
      return;
    }

    // Standard flow: process requirements then final transaction
    // Use the cached signature requirements to avoid re-signing on retry
    let signatures_0: RequirementSignature[] | undefined = cachedSignatureRequirements.current;
    if (typeof startStep_1 === 'number') {
      const isInvalidStep = isInvalidResumeStep(startStep_1, requirements_3.length);
      if (isInvalidStep) {
        throw new Error(`Invalid step: ${startStep_1}`);
      }
      signatures_0 = await processRequirements({
        requirements: requirements_3,
        startStep: startStep_1,
        web3Params: web3Params_7,
        flowActionType: actionType_1,
        flowTracking: flowTracking_4,
        isStaleSubmit: isStaleSubmit_8,
        isSupersededSubmit: isSupersededSubmit_4
      });
      if (!signatures_0) return; // Requirement failed (or the submit was superseded mid-prompt)
    }

    // Final boundary: stop a cancel/reset between the last requirement and the funds-moving tx.
    if (isStaleSubmit_8()) return;
    const result_1 = await executeFinalTransaction({
      requirements: requirements_3,
      transaction: transaction_1,
      signatures: signatures_0,
      web3Params: web3Params_7,
      actionType: actionType_1,
      flowTracking: flowTracking_4,
      exitType: exitType_1,
      skipSimulation: effectiveSkipFinalSimulation,
      requiresSuccessfulSimulation: requiresSuccessfulSimulation_1,
      isStaleSubmit: isStaleSubmit_8,
      isSupersededSubmit: isSupersededSubmit_4
    });
    if (!result_1) return;
    refreshAfterSuccess(result_1.receipt);
  };
  const reset = () => {
    // Invalidate any in-flight submit so a wallet prompt resolved after reset aborts on resume.
    submitGenerationRef.current++;
    setActiveTransactions(undefined);
    checkoutTrackingRef.current = undefined;
    setCheckoutTracking(undefined);
    setTxHash(undefined);
    setReceipt(undefined);
    cachedSignatureRequirements.current = [];
    completedOnChainSteps.current = new Set();
    minedRequirementBlock.current = undefined;
    minedRequirementTxHashes.current = [];
    setSignatureDeadline(undefined);
    setAccount(undefined);
    setStep(0);
    setStatus(undefined);
    setIsAcceptableSimulationError(false);
    setRetryBlocked(false);
    setIsStaleNonceError(false);
    setIsLimitViolationError(false);
    setIsUnverifiedSimulationError(false);
    setIsUnconfirmedTransaction(false);
    setSafeProposalMessage(undefined);
    setIsAwaitingSafeExecution(false);
    setIsSafeStatusUnavailable(false);
    setScreeningError(undefined);
    setIsScreening(false);
  };

  // A same-address connector switch between submit and retry must be attributed to the
  // connector that actually signs the retry.
  const resolveRetryAccount = (submitted: MorphoAccount) => targetAccount?.address && submitted.address && isAddressEqual(targetAccount.address, submitted.address) ? targetAccount : submitted;
  const retry = async (client_5: WalletClient, options?: RetryOptions) => {
    if (!activeTransactions) throw new Error('No active transactions');
    if (!account) throw new Error('No account');
    if (retryBlocked) throw new Error('Retry is blocked until the flow is reset');
    const nextCheckoutTracking = checkoutTrackingRef.current ? {
      ...checkoutTrackingRef.current,
      attemptId: crypto.randomUUID()
    } : undefined;

    // Each retry mints a NEW attemptId, and the checkout funnel joins step/cancellation/conversion
    // events by attempt — so without a matching `review_confirmed` this attempt's outcomes dangle as
    // an orphan and drop out of the funnel. Emit it here (the reviewSessionId carries over, so
    // per-session dedup still holds) to keep the fresh attempt a complete funnel entry.
    if (nextCheckoutTracking) trackReviewConfirmed(nextCheckoutTracking);
    await submitTransactions(client_5, {
      transaction: activeTransactions.transaction,
      requirements: activeTransactions.requirements,
      actionType: activeTransactions.actionType,
      flow: activeTransactions.flow,
      sdkAction: activeTransactions.sdkAction,
      requiresSuccessfulSimulation: activeTransactions.requiresSuccessfulSimulation,
      skipFinalSimulation: activeTransactions.skipFinalSimulation,
      onTxSuccess: activeTransactions.onTxSuccess,
      startStep: step,
      isRetry: true,
      chain,
      account: resolveRetryAccount(account),
      skipSimulation: activeTransactions.requiresSuccessfulSimulation ? false : options?.skipSimulation,
      checkoutTracking: nextCheckoutTracking
    });
  };

  // Re-enter the flow from scratch after a screening OUTAGE (`SCREENING_UNAVAILABLE_MESSAGE`).
  // Unlike `retry`, which resumes an already-screened attempt with `isRetry` (deliberately skipping
  // the gate), this issues a FRESH submit so the signer is screened again — letting the user recover
  // from a transient screening failure in place, without closing and reopening the review dialog.
  const retryScreening = async (client_6: WalletClient) => {
    if (!activeTransactions || !account) return;
    const nextCheckoutTracking_0 = checkoutTrackingRef.current ? {
      ...checkoutTrackingRef.current,
      attemptId: crypto.randomUUID()
    } : undefined;

    // Fresh attemptId → emit `review_confirmed` so the retried attempt isn't an orphan in the
    // checkout funnel (see `retry` for the full rationale).
    if (nextCheckoutTracking_0) trackReviewConfirmed(nextCheckoutTracking_0);
    await submitTransactions(client_6, {
      transaction: activeTransactions.transaction,
      requirements: activeTransactions.requirements,
      actionType: activeTransactions.actionType,
      flow: activeTransactions.flow,
      sdkAction: activeTransactions.sdkAction,
      requiresSuccessfulSimulation: activeTransactions.requiresSuccessfulSimulation,
      skipFinalSimulation: activeTransactions.skipFinalSimulation,
      onTxSuccess: activeTransactions.onTxSuccess,
      chain,
      account: resolveRetryAccount(account),
      checkoutTracking: nextCheckoutTracking_0
      // isRetry defaults to false → fresh entry → the screening gate runs again.
    });
  };

  // Re-enable retry button after cooldown
  useEffect(() => {
    if (!retryDisabled) return;
    const timer = setTimeout(() => {
      setRetryDisabled(false);
    }, RETRY_COOLDOWN);
    return () => clearTimeout(timer);
  }, [retryDisabled]);
  const value: TransactionContextV2Props = {
    activeTransactions,
    checkoutTracking,
    submitTransactions,
    reset,
    retry,
    retryScreening,
    txHash,
    receipt,
    chainId: chain.id,
    account,
    retryDisabled: retryDisabled || retryBlocked,
    status,
    step,
    isAcceptableSimulationError,
    signatureDeadline,
    isStaleNonceError,
    isLimitViolationError,
    isUnverifiedSimulationError,
    isUnconfirmedTransaction,
    safeProposalMessage,
    isAwaitingSafeExecution,
    isSafeStatusUnavailable,
    screeningError,
    isScreening
  };
  return <TransactionContextV2.Provider value={value}>{children}</TransactionContextV2.Provider>;
};
export default TransactionContextV2Provider;